import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { partidasAPI, golpesAPI } from '../services/api';
import socketService from '../services/socket';
import {
  formatVsPar,
  handicapDeJuego,
  nombreResultado,
  parDelCampo,
  parPersonal,
  ventajaEnHoyo
} from '../utils/handicap';
import './Game.css';

function Game() {
  const navigate = useNavigate();
  const { codigo } = useParams();
  const [partida, setPartida] = useState(null);
  const [hoyoActual, setHoyoActual] = useState(1);
  const [golpes, setGolpes] = useState(0);
  const [drops, setDrops] = useState(0);
  const [mulligans, setMulligans] = useState(0);
  const [mulligansDisponibles, setMulligansDisponibles] = useState(3);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRanking, setShowRanking] = useState(false);
  const [finishing, setFinishing] = useState(false);

  const jugadorId = Number(localStorage.getItem('jugadorId'));
  const jugadorNombre = localStorage.getItem('jugadorNombre');

  useEffect(() => {
    loadPartida();
    loadRanking();
    joinSocket();

    // Escuchar eventos de Socket.IO
    socketService.on('golpe-actualizado', handleGolpeActualizado);
    socketService.on('drop-registrado', handleActionRegistered);
    socketService.on('mulligan-registrado', handleActionRegistered);
    socketService.on('ranking-actualizado', handleRankingActualizado);
    socketService.on('partida-finalizada', handlePartidaFinalizada);

    return () => {
      socketService.off('golpe-actualizado', handleGolpeActualizado);
      socketService.off('drop-registrado', handleActionRegistered);
      socketService.off('mulligan-registrado', handleActionRegistered);
      socketService.off('ranking-actualizado', handleRankingActualizado);
      socketService.off('partida-finalizada', handlePartidaFinalizada);
    };
  }, [codigo]);

  useEffect(() => {
    loadGolpesHoyo();
  }, [hoyoActual, jugadorId]);

  const loadPartida = async () => {
    try {
      const response = await partidasAPI.getByCode(codigo);
      setPartida(response.data);

      if (response.data.estado === 'finalizada') {
        navigate(`/resultados/${codigo}`);
      }

      // Cargar mulligans disponibles del jugador
      const jugador = response.data.jugadores.find(j => j.id === jugadorId);
      if (jugador) {
        setMulligansDisponibles(jugador.mulligans_max - jugador.mulligans_usados);
      }
    } catch (err) {
      console.error('Error al cargar partida:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadGolpesHoyo = async () => {
    try {
      const response = await golpesAPI.getByJugador(jugadorId);
      const golpeHoyo = response.data.find(g => g.hoyo_numero === hoyoActual);

      if (golpeHoyo) {
        setGolpes(golpeHoyo.num_golpes);
        setDrops(golpeHoyo.drops);
        setMulligans(golpeHoyo.mulligans);
      } else {
        setGolpes(0);
        setDrops(0);
        setMulligans(0);
      }
    } catch (err) {
      console.error('Error al cargar golpes:', err);
    }
  };

  const loadRanking = async () => {
    try {
      const response = await partidasAPI.getRanking(codigo);
      setRanking(response.data);
    } catch (err) {
      console.error('Error al cargar ranking:', err);
    }
  };

  const joinSocket = () => {
    socketService.joinPartida(codigo);
  };

  const handleGolpeActualizado = useCallback((data) => {
    // Recargar ranking cuando alguien actualiza sus golpes
    loadRanking();
  }, []);

  const handleActionRegistered = useCallback(() => {
    loadRanking();
  }, []);

  const handleRankingActualizado = useCallback((data) => {
    setRanking(data.ranking);
  }, []);

  const handlePartidaFinalizada = () => {
    navigate(`/resultados/${codigo}`);
  };

  const incrementarGolpe = async () => {
    try {
      await golpesAPI.incrementar({
        jugadorId,
        hoyoNumero: hoyoActual
      });

      setGolpes(prev => prev + 1);

      // Notificar via Socket.IO
      socketService.actualizarGolpe(codigo, jugadorId, hoyoActual, golpes + 1);

      // Actualizar ranking
      loadRanking();
    } catch (err) {
      console.error('Error al incrementar golpe:', err);
    }
  };

  const decrementarGolpe = async () => {
    if (golpes === 0) return;

    try {
      await golpesAPI.decrementar({
        jugadorId,
        hoyoNumero: hoyoActual
      });

      setGolpes(prev => Math.max(0, prev - 1));

      socketService.actualizarGolpe(codigo, jugadorId, hoyoActual, Math.max(0, golpes - 1));
      loadRanking();
    } catch (err) {
      console.error('Error al decrementar golpe:', err);
    }
  };

  const registrarDrop = async () => {
    try {
      await golpesAPI.registrarDrop({
        jugadorId,
        hoyoNumero: hoyoActual
      });

      setGolpes(prev => prev + 1);
      setDrops(prev => prev + 1);

      socketService.registrarDrop(codigo, jugadorId, hoyoActual);
      loadRanking();
    } catch (err) {
      console.error('Error al registrar drop:', err);
    }
  };

  const registrarMulligan = async () => {
    if (mulligansDisponibles === 0) {
      alert('No te quedan mulligans disponibles');
      return;
    }

    try {
      await golpesAPI.registrarMulligan({
        jugadorId,
        hoyoNumero: hoyoActual
      });

      setMulligans(prev => prev + 1);
      setMulligansDisponibles(prev => prev - 1);

      socketService.registrarMulligan(codigo, jugadorId, hoyoActual);
    } catch (err) {
      console.error('Error al registrar mulligan:', err);
      alert(err.response?.data?.error || 'Error al registrar mulligan');
    }
  };

  const cambiarHoyo = (nuevoHoyo) => {
    setHoyoActual(nuevoHoyo);
    socketService.cambiarHoyo(codigo, jugadorId, nuevoHoyo);
  };

  const finalizarPartida = async () => {
    if (!window.confirm('¿Estás seguro de finalizar la partida?')) {
      return;
    }

    setFinishing(true);

    try {
      const response = await partidasAPI.finish(codigo);
      socketService.finalizarPartida(codigo, response.data.estadisticas);
      navigate(`/resultados/${codigo}`);
    } catch (err) {
      console.error('Error al finalizar partida:', err);
      alert('Error al finalizar la partida');
      setFinishing(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#2e7d32' }}>Cargando partida...</p>
      </div>
    );
  }

  const hoyoInfo = partida?.hoyos?.find(h => h.numero === hoyoActual);
  const miPosicion = ranking.findIndex(j => j.jugador_id === jugadorId) + 1;

  // Reparto de golpes: el handicap del jugador se aplica hoyo a hoyo según su índice
  const miJugador = partida?.jugadores?.find(j => j.id === jugadorId);
  const miTarjeta = ranking.find(j => j.jugador_id === jugadorId);
  const parCampo = parDelCampo(partida?.hoyos);
  const miHandicapJuego = handicapDeJuego(miJugador?.handicap);
  const miPar = parPersonal(parCampo, miJugador?.handicap);
  const ventajaHoyo = hoyoInfo ? ventajaEnHoyo(miHandicapJuego, hoyoInfo.handicap) : 0;
  const parJugadorHoyo = hoyoInfo ? hoyoInfo.par + ventajaHoyo : 0;
  const claseVsPar = (valor) => (valor < 0 ? 'bajo-par' : valor > 0 ? 'sobre-par' : 'en-par');
  const totalHoyos = partida?.hoyos?.length || partida?.num_hoyos || 18;

  return (
    <div className="game">
      <div className="game-header">
        <div className="header-top">
          <h2>{partida?.campo_nombre}</h2>
          <button className="ranking-toggle" onClick={() => setShowRanking(!showRanking)}>
            {showRanking ? '🏌️' : '🏆'} {showRanking ? 'Jugar' : 'Ranking'}
          </button>
        </div>
        <div className="player-info-header">
          <span>{jugadorNombre}</span>
          {miTarjeta && (
            <span className="score-badge" title={`Tu par: ${miPar}`}>
              {formatVsPar(miTarjeta.vs_par)}
            </span>
          )}
          {miPosicion > 0 && <span className="position-badge">#{miPosicion}</span>}
        </div>
      </div>

      {showRanking ? (
        <div className="container">
          <div className="card ranking-view fade-in">
            <h2>🏆 Ranking</h2>
            <div className="ranking-list">
              {ranking.map((jugador, index) => (
                <div
                  key={jugador.jugador_id}
                  className={`ranking-item ${jugador.jugador_id === jugadorId ? 'current-player' : ''}`}
                >
                  <div className="ranking-position">
                    {index + 1}
                    {index === 0 && <span className="medal">🥇</span>}
                    {index === 1 && <span className="medal">🥈</span>}
                    {index === 2 && <span className="medal">🥉</span>}
                  </div>
                  <div className="ranking-info">
                    <div className="ranking-name">{jugador.nombre}</div>
                    <div className="ranking-stats">
                      Golpes: {jugador.total_golpes} | Neto: {jugador.puntuacion_neta}
                      {' | '}
                      <span className={`vs-par ${claseVsPar(jugador.vs_par)}`}>
                        {formatVsPar(jugador.vs_par)}
                      </span>
                    </div>
                    <div className="ranking-stats secundaria">
                      Su par: {jugador.par_personal} · {jugador.puntos_stableford} pts ·
                      {' '}{jugador.hoyos_jugados}/{partida?.hoyos?.length || 18} hoyos
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="container">
          <div className="hoyo-selector">
            <button
              className="hoyo-nav-btn"
              onClick={() => cambiarHoyo(Math.max(1, hoyoActual - 1))}
              disabled={hoyoActual === 1}
            >
              ◀
            </button>
            <div className="hoyo-info">
              <div className="hoyo-numero">Hoyo {hoyoActual}</div>
              {hoyoInfo && (
                <>
                  <div className="hoyo-details">
                    Par {hoyoInfo.par} | Índice {hoyoInfo.handicap}
                  </div>
                  <div className="hoyo-par-jugador">
                    Tu par: <strong>{parJugadorHoyo}</strong>
                    {ventajaHoyo > 0 && <span className="ventaja-badge">+{ventajaHoyo}</span>}
                  </div>
                </>
              )}
            </div>
            <button
              className="hoyo-nav-btn"
              onClick={() => cambiarHoyo(Math.min(totalHoyos, hoyoActual + 1))}
              disabled={hoyoActual === totalHoyos}
            >
              ▶
            </button>
          </div>

          <div className="golpes-counter fade-in">
            <div className="golpes-label">Golpes</div>
            <button className="golpe-btn" onClick={incrementarGolpe}>
              <div className="golpes-value">{golpes}</div>
            </button>
            {golpes > 0 && hoyoInfo ? (
              <div className={`golpes-resultado ${claseVsPar(golpes - parJugadorHoyo)}`}>
                {nombreResultado(golpes, parJugadorHoyo, ventajaHoyo > 0)}
                {golpes !== parJugadorHoyo &&
                  ` · ${formatVsPar(golpes - parJugadorHoyo)} sobre tu par`}
              </div>
            ) : (
              <div className="golpes-hint">Toca para añadir golpe</div>
            )}

            {golpes > 0 && (
              <button className="undo-btn" onClick={decrementarGolpe}>
                ↶ Deshacer último golpe
              </button>
            )}
          </div>

          <div className="actions-panel">
            <div className="action-stats">
              {drops > 0 && <div className="stat-badge drop">Drops: {drops}</div>}
              {mulligans > 0 && <div className="stat-badge mulligan">Mulligans: {mulligans}</div>}
              <div className="stat-badge available">
                Mulligans disponibles: {mulligansDisponibles}/3
              </div>
            </div>

            <div className="action-buttons">
              <button className="btn btn-danger action-btn" onClick={registrarDrop}>
                Drop (+1 penalización)
              </button>
              <button
                className="btn btn-warning action-btn"
                onClick={registrarMulligan}
                disabled={mulligansDisponibles === 0}
              >
                Mulligan (sin penalización)
              </button>
            </div>
          </div>

          <div className="hoyo-nav-inferior">
            <button
              className="btn btn-secondary"
              onClick={() => cambiarHoyo(Math.max(1, hoyoActual - 1))}
              disabled={hoyoActual === 1}
            >
              ◀ Anterior
            </button>
            <button
              className="btn btn-primary"
              onClick={() => cambiarHoyo(Math.min(totalHoyos, hoyoActual + 1))}
              disabled={hoyoActual === totalHoyos}
            >
              Siguiente hoyo ▶
            </button>
          </div>

          <button
            className="btn btn-primary btn-full finish-btn"
            onClick={finalizarPartida}
            disabled={finishing}
          >
            {finishing ? 'Finalizando...' : '🏁 Finalizar Partida'}
          </button>
        </div>
      )}
    </div>
  );
}

export default Game;
